import type { ReactNode } from "react";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { Button } from "@foodpilot/foods";
import { Box, Divider, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import { Breadcrumbs } from "src/components/Breadcrumb/Breadcrumbs";
import { EntityFormPage } from "src/components/EntityForm/EntityFormPage";
import { FormHeader } from "src/components/EntityForm/FormHeader";

import { PackagingScorePreviewCard } from "./PackagingScorePreviewCard";
import type { Packaging } from "src/api/client-api/packagings/useGetOnePackaging";
import type { PackagingPreviewScores } from "src/api/client-api/packagings/useCalculatePackagingPreview";

type PackagingFormLayoutProps = {
    appearance?: "legacy" | "product-v2";
    title: string;
    activeBreadcrumb: string;
    companyName: string;
    onCancel: () => void;
    onSaveLabel: string;
    onCancelLabel: string;
    saveDisabled?: boolean;
    isSaving?: boolean;
    onSubmit: () => void;
    packaging?: Packaging;
    previewScores?: PackagingPreviewScores;
    isPreviewLoading?: boolean;
    children: ReactNode;
};

export const PackagingFormLayout = ({
    appearance = "legacy",
    title,
    activeBreadcrumb,
    companyName,
    onCancel,
    onSaveLabel,
    onCancelLabel,
    saveDisabled = false,
    isSaving = false,
    onSubmit,
    packaging,
    previewScores,
    isPreviewLoading,
    children,
}: PackagingFormLayoutProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const submitIfAllowed = () => {
        if (saveDisabled || isSaving) return;
        onSubmit();
    };

    return (
        <Box
            component="form"
            data-testid="packaging-form"
            sx={appearance === "legacy" ? { minHeight: "100%", backgroundColor: theme.custom.grey[300] } : undefined}
            onSubmit={(event) => {
                event.preventDefault();
                submitIfAllowed();
            }}
        >
            {appearance === "product-v2" ?
                <EntityFormPage
                    header={(ref) => (
                        <FormHeader
                            ref={ref}
                            breadcrumbs={[
                                {
                                    label: t("packagings.tabs.all", "All packagings"),
                                    action: onCancel,
                                },
                                { label: activeBreadcrumb },
                            ]}
                            title={title}
                            metadata={{
                                label: t("product.v2.informations.owning_entity", "Owning entity:"),
                                value: companyName,
                            }}
                            onCancel={onCancel}
                            onSave={submitIfAllowed}
                            cancelLabel={onCancelLabel}
                            saveLabel={onSaveLabel}
                            saveDisabled={saveDisabled}
                            saving={isSaving}
                            testIdPrefix="packaging"
                        />
                    )}
                    aside={({ isNarrow, stickyTop }) => (
                        <PackagingScorePreviewCard
                            appearance="product-v2"
                            packaging={packaging}
                            previewScores={previewScores}
                            isPreviewLoading={isPreviewLoading}
                            isNarrow={isNarrow}
                            stickyTop={stickyTop}
                        />
                    )}
                >
                    {children}
                </EntityFormPage>
            :   <>
                    <Box
                        sx={{
                            px: { xs: 3, lg: 6 },
                            py: 3,
                            borderBottom: `1px solid ${theme.custom.grey[500]}`,
                            backgroundColor: theme.custom.grey[100],
                        }}
                    >
                        <Breadcrumbs
                            items={[
                                { label: t("packagings.tabs.all", "All packagings"), action: onCancel },
                                { label: activeBreadcrumb },
                            ]}
                        />
                    </Box>

                    <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={3}
                        alignItems={{ xs: "stretch", md: "center" }}
                        justifyContent="space-between"
                        sx={{ px: { xs: 3, lg: 6 }, py: 3 }}
                    >
                        <Stack
                            direction={{ xs: "column", md: "row" }}
                            spacing={2}
                            alignItems={{ md: "center" }}
                            flex={1}
                        >
                            <Typography variant="h2">{title}</Typography>
                            <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", md: "block" } }} />
                            <Stack direction="row" spacing={1}>
                                <Typography variant="body2" sx={{ fontWeight: theme.typography.fontWeightMedium }}>
                                    {t("product.v2.informations.owning_entity", "Owning entity:")}
                                </Typography>
                                <Typography variant="body2" sx={{ color: theme.custom.grey[2800] }}>
                                    {companyName}
                                </Typography>
                            </Stack>
                        </Stack>
                        <Stack direction="row" spacing={2} justifyContent="flex-end">
                            <Button onClick={onCancel} startIcon={<CloseIcon />}>
                                {onCancelLabel}
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={saveDisabled || isSaving}
                                startIcon={<CheckIcon />}
                            >
                                {onSaveLabel}
                            </Button>
                        </Stack>
                    </Stack>

                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", lg: `minmax(0, 1fr) ${theme.spacing(45)}` },
                            gap: 3,
                            px: { xs: 3, lg: 6 },
                            pb: 6,
                            backgroundColor: theme.custom.grey[300],
                        }}
                    >
                        <Stack spacing={3}>{children}</Stack>
                        <PackagingScorePreviewCard
                            appearance="legacy"
                            packaging={packaging}
                            previewScores={previewScores}
                            isPreviewLoading={isPreviewLoading}
                        />
                    </Box>
                </>
            }
        </Box>
    );
};
